import { UserOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  message,
  Modal,
  PageHeader,
  Popconfirm,
  Tag,
  Typography,
} from "antd";
import "assets/fonticons/style.css";
import PdfLogo from "assets/images/pdflogo.png";
import LayoutCss from "layout/layout.module.scss";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import BillingApproval from "redux/models/billingApproval";
import Invoice from "redux/models/invoice";
import K from "utilities/constants";
import {
  calculateDaysDifference,
  convertIntoDashUSFormat,
  convertIntoUSFormat,
  getMonthName,
} from "utilities/dateUtility";
import {
  isPermissionPresent,
  removeUnderScore,
} from "utilities/generalUtility";
import { displayDollar } from "utilities/tableUtility";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { selectUser } from "redux/slices/userSlice";
import { useSelector } from "react-redux";
import Export from "redux/models/export";
import styles from "../clientInvoices.module.scss";
import BillingDetails from "../../submitToBilling/billingDetails";
import { EditInvoiceButton } from "./editInvoiceButton/editInvoiceButton";
import InvoiceClientBills from './invoiceClientBills/invoiceClientBills';
import InvoiceJobBills from './invoiceJobBills/invoiceJobBills';

const { Title, Paragraph, Text } = Typography;
const {
  Invoice: { Type },
  BillingType,
} = K;

const tagColors = {
  CONFLICTED: "error",
  DRAFT: "warning",
  PAID: "success",
  PENDING: "blue",
};

export default function InvoiceDetails() {
  const { id } = useParams();
  const inputRef = useRef(null);
  const gridRef = useRef(null);

  const userSlice = useSelector(selectUser);
  const history = useHistory();
  const [pageData, setPageData] = useState({
    details: null,
    overDueDays: 0,
    isEditMode: false,
    selectedRecord: null,
    conversionHistory: [],
    isModalVisible: false,
    isClientBillingRow: false,
    isBillingModalVisible: false,
    isReadOnly: true,
  });
  const { details } = pageData;

  const getBillHistory = async (payload) => {
    try {
      return await BillingApproval.getSubmittedBillHistory(payload);
    } catch (err) {
      console.error(err);
    }
  };

  const editAmount = async () => {
    const payload = {
      edit: true,
      dublicateAmount: +inputRef.current.value,
      currency: pageData.selectedRecord.convertToCurrency,
    };
    try {
      const res = pageData.isClientBillingRow
        ? await BillingApproval.editClientSubmittedBilling({
            ...payload,
            clientSubmittedBillId: pageData.selectedRecord.id,
          })
        : await BillingApproval.editJobSubmittedBilling({
            ...payload,
            jobSubmittedBillId: pageData.selectedRecord.id,
          });
      const history = await getBillHistory({
        type: pageData.isClientBillingRow ? Type.Client : Type.Job,
        billingId: res.id,
      });
      getInvoiceDetails();

      if (!pageData.isClientBillingRow) {
        gridRef.current.api.refreshServerSideStore({ purge: true });
      }

      setPageData((prev) => {
        return {
          ...prev,
          selectedRecord: res,
          conversionHistory: history,
          isEditMode: !prev.isEditMode,
        };
      });
    } catch (err) {
      message.error("Failed to edit amount.");
      console.error(err);
    }
  };

  const toggleEdit = () => {
    setPageData((prev) => ({ ...prev, isEditMode: !prev.isEditMode }));
  };

  const deleteInvoice = async () => {
    try {
      await Invoice.delete({
        invoiceId: id,
        type: pageData.isClientBillingRow ? Type.Client : Type.Job,
      });
      history.goBack();
      message.success("Invoice deleted successfully");
    } catch (err) {
      setPageData((prev) => ({ ...prev, isModalVisible: false }));
      console.error(err);
    }
  };

  const getInvoiceDetails = async () => {
    try {
      const res = await Invoice.getInvoiceDetail(id);
      setPageData((prev) => ({
        ...prev,
        details: res,
        isClientBillingRow: res.type === Type.Client,
        overDueDays: calculateDaysDifference(res.dueDate),
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleInvoiceDetail = async (id) => {
    try {
      await Export.getReportInvoiceDetail(id);
      message.success(
        "Invoice Detail export request successfully received! Check your email shortly",
      );
    } catch (error) {
      console.log(error);
      message.success("something went wrong");
    }
  };

  useEffect(() => {
    getInvoiceDetails();
  }, [id]);

  const billModalPrepare = async (record) => {
    const history = await getBillHistory({
      type: pageData.isClientBillingRow ? Type.Client : Type.Job,
      billingId: record.id,
    });
    setPageData((prev) => ({
      ...prev,
      selectedRecord: record,
      conversionHistory: history,
      isBillingModalVisible: true,
      isReadOnly: !!details?.invoiceNumber,
    }));
  }

  return (
    <>
      {pageData.isClientBillingRow && (
      <div className="pdf-wrap">
        <div className="pdf-header">
          <div className="pdf-heading">
            <Title level={5}>Source 2</Title>
          </div>
          <div className="pdf-header-img">
            <img src={PdfLogo} alt="" />
          </div>
        </div>
        <div className="report-list-wrap">
          <Paragraph className="report-para">
            941 W Morse Boulevard, Suite 100
            <br />
            Winter Park, FL 32789 US
            <br />
            accounting@source2.com
            <br /> www.source2.com
          </Paragraph>
          <Title level={2}>INVOICE</Title>
          <Title level={5}>BILL TO</Title>
          <ul className="report-list-ul">
            <li>
              <b>Client ID:</b>
              <Text>{details?.client.id}</Text>
            </li>
            <li>
              <b>INVOICE #</b>
              <Text>{details?.invoiceNumber ?? K.NullPlaceholder}</Text>
            </li>
          </ul>
          <ul className="report-list-ul">
            <li>
              <b>Client Name:</b>
              <Text>{details?.client.name}</Text>
            </li>
            <li>
              <b>DATE</b>
              <Text>{convertIntoUSFormat(details?.startDate)}</Text>
            </li>
          </ul>

          <ul className="report-list-ul">
            <li>
              <b>Status:</b>
              <Text>
                <Tag color={tagColors[details?.status]}>
                  {removeUnderScore(details?.status)}
                </Tag>
              </Text>
            </li>
            <li>
              <b>DUE DATE</b>
              <Text>{convertIntoUSFormat(details?.dueDate)}</Text>
            </li>
          </ul>
        </div>
        <div className="header-divider" />
      </div>
        )}

      <div className={styles.wrapPageHeader}>
        <PageHeader
          className={LayoutCss.appPageHeader}
          ghost={false}
          onBack={() => window.history.back()}
          title="Invoice Details"
          extra={
            <div className={styles.invoiceModal}>
              {isPermissionPresent(
                K.Permissions.EditInvoice,
                userSlice.roles,
              ) && (
                <EditInvoiceButton
                  invoiceDetails={details}
                  onSuccess={(invoice) =>
                    setPageData((prev) => ({
                      ...prev,
                      details: { ...prev.details, ...invoice },
                    }))
                  }
                />
              )}
              {isPermissionPresent(
                K.Permissions.DeleteInvoice,
                userSlice.roles,
              ) && (
                <Popconfirm
                  placement="bottom"
                  title="Are you sure you want to delete this invoice?"
                  onConfirm={deleteInvoice}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button style={{ marginLeft: 10 }} type="primary" danger>
                    Delete
                  </Button>
                </Popconfirm>
              )}
            </div>
          }
        />
      </div>
      <div className={styles.topInfoCard}>
        <Card className={styles.generateInvoiceCard}>
          <div className={styles.topCardDetail}>
            <div className={styles.cardBox}>
              <ul>
                <li>
                  <span className={styles.cardIdDate}> Invoice #: </span>
                  <span className={styles.cardIdDate}>
                    {details?.invoiceNumber ?? K.NullPlaceholder}
                  </span>
                </li>
                <li>
                  Invoice Date:
                  <span>
                    {convertIntoDashUSFormat(details?.dueDate, false)}
                  </span>
                </li>
                <li>
                  Invoice Month:
                  <span>{getMonthName(details?.dueDate)}</span>
                </li>
                <li>
                  <span className={styles.cardIdDate}>Status:</span>
                  <span className={styles.cardIdDate}>
                    <Tag color={tagColors[details?.status]}>
                      {removeUnderScore(details?.status)}
                    </Tag>
                  </span>
                </li>
              </ul>
            </div>
            <div className={styles.cardRightSide}>
              <ul>
                <li className={`${styles.cardDetails} ${styles.clientBalance}`}>
                  Client:{" "}
                </li>
                <li> {details?.client.name}</li>
                <li className={styles.cardDetails}>Balance:</li>
                <li
                  className={`${styles.cardDetails} ${styles.invoicesDetails}`}
                >
                  {displayDollar(+details?.total.toFixed(2) ?? 0)}
                </li>
                <div className={styles.invoicesPrint}>
                  {pageData.isClientBillingRow && (
                    <Button
                      type="link"
                      className={`${styles.cardDetails} hover-underlined`}
                      onClick={() => {
                        window.print();
                      }}
                    >
                      PDF
                    </Button>
                  )}
                  {!pageData.isClientBillingRow && (
                    <Button
                      type="link"
                      className={`${styles.cardDetails} hover-underlined`}
                      onClick={() => {
                        handleInvoiceDetail(id);
                      }}
                    >
                      Excel
                    </Button>
                  )}
                </div>
              </ul>
            </div>
          </div>
        </Card>
      </div>

      <Card className={styles.bottomInfoCard}>
        <div className={styles.bottomCardList}>
          <div className={styles.bottomCardIcons}>
            <ul>
              <li>
                <div className={styles.invoiceIcon}>
                  <i className="icon-invoice" />
                </div>
                <div className={styles.invoiceIconDetail}>
                  <p className={styles.listName}>Invoice Number</p>
                  <Title level={5} className={styles.listDes}>
                    {details?.invoiceNumber ?? K.NullPlaceholder}
                  </Title>
                </div>
              </li>
              <li>
                <div className={styles.invoiceIcon}>
                  <UserOutlined className={styles.cardIcon} />
                </div>
                <div className={styles.invoiceIconDetail}>
                  <p className={styles.listName}>Client</p>
                  <Title level={5} className={styles.listDes}>
                    {details?.client.name}
                  </Title>
                </div>
              </li>
            </ul>
          </div>
        </div>

        { pageData.isClientBillingRow ?
          <InvoiceClientBills
            pageData={pageData}
            billModalPrepare={billModalPrepare}
          /> :
          <InvoiceJobBills
            gridRef={gridRef}
            billModalPrepare={billModalPrepare}
          />
        }
      </Card>

      <Modal
        centered
        width={572}
        footer={false}
        title={details?.client.name}
        className="clientBillingModal modal-overflow-auto"
        open={pageData.isBillingModalVisible}
        onOk={() =>
          setPageData((prev) => ({ ...prev, isBillingModalVisible: false }))
        }
        onCancel={() =>
          setPageData((prev) => ({ ...prev, isBillingModalVisible: false }))
        }
        closeIcon={
          <span className="closeIcon">
            <i className="icon-closeable" />
          </span>
        }
      >
        <BillingDetails
          type={
            pageData.isClientBillingRow ? BillingType.Client : BillingType.Job
          }
          billing={pageData.selectedRecord}
          isEditMode={pageData.isEditMode}
          isReadOnly={pageData.isReadOnly}
          inputRef={inputRef}
          editAmount={editAmount}
          toggleEdit={toggleEdit}
          history={pageData.conversionHistory}
          duplicateAmount={pageData.selectedRecord?.dublicateAmount}
        />
      </Modal>
    </>
  );
}
